import moment from 'moment'
import {
  SINGLE_QUOTES,
  TIME_UNIT_MS,
  DAYS,
  TIME_UNIT_HRS,
  PERCENTAGE_SIGN,
} from '../components/SEMetrics/helpers/constants'
import type {ComponentType} from 'react'
import type React from 'react'
import {lazy} from 'react'

export type StatusType = 'build' | 'deployment' | 'pr' | 'commit' | 'stage'

/**
 * Get standardized display text for statuses across different features
 *
 * @param status Original status string from API
 * @param type Type of status (build, deployment, pr, etc)
 * @returns Standardized display text
 */
type StatusState = 'success' | 'failure' | 'inProgress'
type StatusMap = Record<StatusState, Record<StatusType | 'default', string>>

// Status pattern mappings
const STATUS_PATTERNS: Record<StatusState, readonly string[]> = {
  success: ['success', 'succeeded', 'successful', 'complete', 'completed'],
  failure: ['fail', 'failed', 'failure', 'error', 'errored', 'canceled', 'cancelled'],
  inProgress: ['running', 'in progress', 'pending', 'active', 'started', 'queued'],
} as const

// Status text mappings by type and state
const STATUS_TEXT_MAP: StatusMap = {
  success: {
    build: 'Completed',
    deployment: 'Success',
    pr: 'Merged',
    stage: 'Completed',
    commit: 'Completed',
    default: 'Completed',
  },
  failure: {
    build: 'Failed',
    deployment: 'Failed',
    pr: 'Rejected',
    stage: 'Failed',
    commit: 'Failed',
    default: 'Failed',
  },
  inProgress: {
    build: 'Building',
    deployment: 'Deploying',
    pr: 'Open',
    stage: 'In Progress',
    commit: 'In Progress',
    default: 'In Progress',
  },
}

/**
 * Format concatenated status text with proper spacing
 * @param status The status string to format
 * @returns Formatted status string
 */
const formatStatusText = (status: string): string => {
  // Handle special cases first
  if (status.toLowerCase() === 'partiallysucceeded') {
    return 'Partially Succeeded'
  }

  // Add spaces before capital letters and capitalize first letter
  return status
    .replace(/([A-Z])/g, ' $1') // Add space before capitals
    .replace(/^\s+/, '') // Remove leading space
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/^[a-z]/, c => c.toUpperCase()) // Capitalize first letter
}

export const getStandardizedStatusText = (status: string, type: StatusType): string => {
  if (!status) return 'Unknown'

  // Format special concatenated statuses
  const normalizedStatus = status.toLowerCase().trim()
  if (normalizedStatus === 'partiallysucceeded') {
    return 'Partially Succeeded'
  }

  // Match patterns and get standardized text
  const matchState = (Object.keys(STATUS_PATTERNS) as StatusState[]).find(state =>
    STATUS_PATTERNS[state].some(pattern => normalizedStatus.includes(pattern)),
  )

  if (matchState) {
    return STATUS_TEXT_MAP[matchState][type] || STATUS_TEXT_MAP[matchState].default
  }

  // If no standard mapping found, format the original status text
  return formatStatusText(status)
}

/**
 * Converts a date range to UTC timestamps preserving the day boundaries
 *
 * @param dateFrom Start date
 * @param dateTo End date
 * @returns Object with beginTime and endTime UTC timestamps
 */
export const convertDateRangeToTimestamps = (dateFrom: Date, dateTo: Date): {beginTime: number; endTime: number} => {
  // First get the local dates with their day boundaries
  const localStart = moment(dateFrom).startOf('day')
  const localEnd = moment(dateTo).endOf('day')

  // Create UTC dates with exact same day components
  // This ensures the day doesn't shift due to timezone differences
  const startDate = moment.utc({
    year: localStart.year(),
    month: localStart.month(),
    day: localStart.date(),
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  })

  const endDate = moment.utc({
    year: localEnd.year(),
    month: localEnd.month(),
    day: localEnd.date(),
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 999,
  })

  return {
    beginTime: startDate.valueOf(),
    endTime: endDate.valueOf(),
  }
}

/**
 * Determines the appropriate unit for a given metric name
 *
 * @param metricName The name of the metric
 * @returns The appropriate unit for the metric
 */
export const getMetricUnit = (metricName: string): string => {
  if (metricName.includes('Response Time')) return TIME_UNIT_MS
  if (metricName.includes('Lead Time')) return DAYS
  if (metricName.includes('Mean Time')) return TIME_UNIT_HRS
  if (metricName.includes('Coverage') || metricName.includes('Rate') || metricName.includes('Reusability'))
    return PERCENTAGE_SIGN
  if (metricName.includes('Incidents') || metricName.includes('Code Fix')) return SINGLE_QUOTES
  return SINGLE_QUOTES
}

/**
 * Checks whether a value is null, undefined, or an empty string.
 * Note: 0 and false are NOT considered empty.
 * @param value The value to check
 * @returns true when the value is null, undefined, or an empty string
 */
export const isNullOrEmpty = (value: unknown): boolean => {
  return value === null || value === undefined || value === ''
}

/**
 * Formats a metric value with the appropriate unit
 *
 * @param value The numeric value to format
 * @param unit The unit to append to the value
 * @returns The formatted metric value
 */
export const formatMetricValue = (value: number, unit: string): string => {
  if (unit === TIME_UNIT_MS) return `${Math.round(value)}${unit}`
  if (unit === PERCENTAGE_SIGN) return `${value.toFixed(2)}${unit}`
  if (unit === DAYS || unit === TIME_UNIT_HRS) return `${value.toFixed(2)}${unit}`
  return `${value}${unit}`
}

/**
 * Determines the color for a status tag based on the status value
 * @param status The status string to evaluate
 * @returns The color to use for the status tag
 */
export const getStatusTagColor = (
  status: string | null | undefined,
): 'green' | 'blue' | 'red' | 'orange' | 'gray' | 'default' => {
  if (!status) return 'default' // Default to default for null/undefined status

  const statusLower = status.toLowerCase().trim()

  // Green statuses - Success/Completed states
  if (
    statusLower === 'succeeded' ||
    statusLower === 'success' ||
    statusLower === 'configured' ||
    statusLower === 'completed' ||
    statusLower === 'partiallysucceeded' ||
    statusLower === 'committed' ||
    statusLower === 'validated' ||
    statusLower === 'validated (manual data)' ||
    statusLower === 'passed'
  ) {
    return 'green'
  }

  // Blue statuses - Active/In Progress states
  if (
    statusLower === 'running' ||
    statusLower === 'active' ||
    statusLower === 'new' ||
    statusLower === 'ready for validation' ||
    statusLower === 'pending review' ||
    statusLower === 'flagged for review'
  ) {
    return 'blue'
  }

  // Red statuses - Error/Failed states
  if (
    statusLower.includes('fail') ||
    statusLower === 'error' ||
    statusLower === 'cancelled' ||
    statusLower === 'canceled' ||
    statusLower === 'rejected' ||
    statusLower === 'abandoned' ||
    statusLower === 'removed' ||
    statusLower === 'escalated' ||
    statusLower === 'not validated'
  ) {
    return 'red'
  }

  // Orange statuses - Warning/Pending states
  if (
    statusLower === 'warning' ||
    statusLower === 'partially configured' ||
    statusLower === 'unstable' ||
    statusLower === 'in progress' ||
    statusLower === 'validating' ||
    statusLower === 'blocked' ||
    statusLower.includes('abort') ||
    statusLower === 'validation pending' ||
    statusLower === 'under analysis'
  ) {
    return 'orange'
  }

  // Gray statuses - Inactive/Neutral states
  if (
    statusLower === 'inactive' ||
    statusLower === 'skipped' ||
    statusLower === 'disabled' ||
    statusLower === 'closed' ||
    statusLower === 'done' ||
    statusLower === 'not applicable'
  ) {
    return 'gray'
  }

  return 'default'
}

/**
 * Determines the tag type ('faded' or 'outlined') based on the status
 * @param status The status string to evaluate
 * @returns The tag type to use ('faded' or 'outlined')
 */
export const getStatusTagType = (status: string | null | undefined): 'faded' | 'outlined' => {
  if (!status) return 'faded' // Default to faded for null/undefined status

  const statusLower = status.toLowerCase().trim()

  // Based on the image, these statuses use outlined style
  if (
    statusLower === 'configured' ||
    statusLower === 'partially configured' ||
    statusLower === 'committed' ||
    statusLower === 'new' ||
    statusLower === 'done' ||
    statusLower === 'removed' ||
    statusLower === 'validating' ||
    statusLower === 'validated (manual data)' ||
    statusLower === 'flagged for review' ||
    statusLower === 'pending review' ||
    statusLower === 'under analysis' ||
    statusLower === 'escalated'
  ) {
    return 'outlined'
  }

  // Default to faded for all other statuses
  return 'faded'
}

/**
 * Format duration in seconds to readable format
 * @param durationMs Duration in milliseconds
 * @returns Formatted duration string (e.g., "1h 30m 45s")
 */
export const formatDuration = (durationMs: number): string => {
  const seconds = Math.floor(durationMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  } else {
    return `${seconds}s`
  }
}

/**
 * Formats a duration value in milliseconds to minutes and seconds (MM:SS)
 * @param val Duration value in milliseconds
 * @returns Formatted string in MM:SS format
 */
export const formatToMinute = (val: number | null): string => {
  if (val === null || isNaN(val)) return 'NaN:NaN'
  const minutes = Math.floor(val / 60000)
  const seconds = (val % 60000) / 1000
  return seconds === 60 ? `${minutes + 1}:00` : `${minutes}:${seconds < 10 ? '0' : ''}${seconds.toFixed(0)}`
}

/**
 * Format timestamp with date and time in UTC to ensure consistency across apps
 * This ensures times are displayed the same way as in the Angular app
 *
 * @param timestamp Unix timestamp, string date, or Date object
 * @param format The format string to use (defaults to 'DD/MM/YYYY (hh:mm A)')
 * @returns Formatted date and time string
 */
export const formatTimestampUtc = (
  timestamp: number | string | Date | undefined | null,
  format?: string,
  options?: {dateOnly?: boolean},
): string => {
  if (!timestamp) return ''

  const outputFormat = options?.dateOnly ? 'DD/MM/YYYY' : format || 'DD/MM/YYYY (hh:mm A)'

  let parsedMoment

  // Handle number or Date directly
  if (typeof timestamp === 'number' || timestamp instanceof Date) {
    parsedMoment = moment.utc(timestamp)
  } else if (typeof timestamp === 'string') {
    // Try parsing with known input formats
    const knownFormats = [
      moment.ISO_8601, // Covers 2025-06-27T06:44:33.0
      'DD-MM-YYYY HH:mm:ss', // Covers 16-05-2025 10:38:15
      'DD/MM/YYYY HH:mm:ss', // Optional: if you have this too
    ]
    parsedMoment = moment.utc(timestamp, knownFormats, true)
  } else {
    return ''
  }

  return parsedMoment.isValid() ? parsedMoment.format(outputFormat) : ''
}
const DATE_FORMATS = {
  US_DATE_ONLY: 'MM/DD/YYYY',
  US_DATE_TIME: 'MM/DD/YYYY (hh:mm A)',
} as const

const KNOWN_INPUT_FORMATS = [moment.ISO_8601, 'DD-MM-YYYY HH:mm:ss', 'DD/MM/YYYY HH:mm:ss', 'YYYY-MM-DD', 'DD/MM/YYYY']
export const formatTimestampUS = (
  timestamp: number | string | Date | undefined | null,
  format?: string,
  options?: {dateOnly?: boolean},
): string => {
  if (!timestamp) return ''

  const outputFormat = options?.dateOnly ? DATE_FORMATS.US_DATE_ONLY : format || DATE_FORMATS.US_DATE_TIME

  const parsedMoment =
    typeof timestamp === 'string' ? moment.utc(timestamp, KNOWN_INPUT_FORMATS, true) : moment.utc(timestamp)

  return parsedMoment.isValid() ? parsedMoment.format(outputFormat) : ''
}

/**
 * Formats a timestamp to UTC date format, handling '#NA' values
 * @param date Date string or Date object to format
 * @param format The desired format string for moment
 * @returns Formatted date string or '#NA' if the input was '#NA'
 */
export const convertToUTCDateFormat = (date: string | Date, format: string): string => {
  if (date !== '#NA') {
    return moment(new Date(date)).utc().format(format)
  } else {
    return date
  }
}

/**
 * Creates sorted filter options from a set of strings
 * @param values Set of string values to convert into filter options
 * @param includeAll Whether to include an 'All' option at the start
 * @returns Array of filter options sorted alphabetically
 */
export const getSortedFilterOptions = (values: Set<string>, includeAll = true) => {
  const sortedOptions = Array.from(values)
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({
      id: value.toLowerCase(),
      label: value,
    }))

  return includeAll ? [{id: 'all', label: 'All'}, ...sortedOptions] : sortedOptions
}

/**
 * Calculates how many days ago a date was and returns a formatted string
 * @param dateString The date string to calculate days ago from
 * @returns Formatted string (Today, 1 day ago, or X days ago)
 */
export const getDaysAgo = (dateString: string): string => {
  const buildDate = new Date(dateString)
  const currentDate = new Date()

  // Calculate difference in days - using UTC dates to ignore time portion
  const diffDays = Math.floor(
    (Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()) -
      Date.UTC(buildDate.getFullYear(), buildDate.getMonth(), buildDate.getDate())) /
      (1000 * 60 * 60 * 24),
  )

  if (diffDays === 0) {
    return 'Today'
  } else if (diffDays === 1) {
    return '1 day ago'
  } else {
    return `${diffDays} days ago`
  }
}

/**
 * Groups data by day and returns formatted dates and counts
 * @param data Array of items with timestamp field
 * @param timestampField The field name containing the timestamp (e.g., 'startTime', 'scmCommitTimestamp')
 * @returns Object containing formatted dates and counts arrays
 */
export const groupDataByDay = <T extends Record<string, any>>(data: T[], timestampField: string) => {
  if (!data || data.length === 0) {
    return {formattedDates: [], counts: []}
  }

  // Group data by date
  const groupedData: Record<string, T[]> = {}
  const dates: string[] = []

  // Process each item and group by date
  data.forEach(item => {
    // Format date as YYYY-MM-DD for internal use
    const dateStr = moment(new Date(item[timestampField])).format('YYYY-MM-DD')

    if (!groupedData[dateStr]) {
      groupedData[dateStr] = []
      dates.push(dateStr)
    }

    groupedData[dateStr].push(item)
  })

  // Sort dates chronologically
  dates.sort((a, b) => a.localeCompare(b))

  // Create arrays for chart data
  const formattedDates = dates.map(date => moment(date).format('DD/MM'))
  const counts = dates.map(date => groupedData[date].length)
  return {formattedDates, counts, groupedData, dates}
}

/**
 * Extracts and formats the year range from an array of date labels
 * @param labels Array of date labels (e.g., ["01/12", "02/12"])
 * @returns Formatted year range string (e.g., "- 2025" or "- 24-2025")
 */
export const getYearRangeFromLabels = (labels: string[]): string => {
  if (!labels || labels.length === 0) return ''

  // Extract years from the labels (assuming date format like "Jan 1" or "Jan 1, 2025")
  const years = new Set<number>()

  labels.forEach(label => {
    // Try to extract year from various formats
    const fullDateMatch = label.match(/\d{4}$/) // For "Jan 1, 2025"
    const weekRangeMatch = label.match(/\d{4}/g) // For "Jan 1 - Jan 7, 2025" or "Dec 25, 2024 - Jan 1, 2025"

    if (fullDateMatch) {
      years.add(parseInt(fullDateMatch[0]))
    } else if (weekRangeMatch) {
      // Add all years found in the range
      weekRangeMatch.forEach(year => years.add(parseInt(year)))
    } else {
      // If no explicit year, assume current year
      const currentYear = new Date().getFullYear()
      years.add(currentYear)
    }
  })

  const sortedYears = Array.from(years).sort((a, b) => a - b)

  if (sortedYears.length === 0) {
    return ''
  } else if (sortedYears.length === 1) {
    return `- ${sortedYears[0]}`
  } else {
    // For consecutive years like 2024-2025, abbreviate the first
    if (sortedYears.length === 2 && sortedYears[1] - sortedYears[0] === 1) {
      return `- ${sortedYears[0].toString().slice(-2)}-${sortedYears[1]}`
    } else {
      // For non-consecutive years or more than 2 years
      return `- ${sortedYears.join(', ')}`
    }
  }
}

/**
 * Sets a date's time to midnight (00:00:00.000)
 * @param date The date to set to midnight
 * @returns A new Date object set to midnight
 */
export const toMidnight = (date: Date): Date => {
  date.setHours(0, 0, 0, 0)
  return date
}

/**
 * Checks if a build's end time is after or equal to a given date
 * @param build The build object containing endTime
 * @param date The date to compare against
 * @returns boolean indicating if the build is after or equal to the date
 */
export const checkBuildAfterDate = (build: any, date: Date): boolean => {
  const d = new Date(build.endTime).toUTCString()
  return new Date(d).getTime() >= date.getTime()
}

export function getValueAbbreviationParser(value: number | string): string {
  const num = Number(value)
  const absValue = Math.abs(num)
  const abbreviation = ['T', 'B', 'M', 'K']
  const list = [10 ** 12, 10 ** 9, 10 ** 6, 10 ** 3]

  for (let i = 0; i < list.length; i++) {
    if (absValue >= list[i]) {
      return (absValue / list[i]).toFixed(1) + abbreviation[i]
    }
  }

  const result = absValue.toFixed(1)
  return result.endsWith('.0') ? result.slice(0, -2) : result
}

/**
 * Joins array-like values into a comma-separated string.
 * Ensures falsy and empty values are removed before joining.
 *
 * @param values Values to join (array or single value)
 * @param separator Separator to use between values (default: ", ")
 * @returns Joined string or empty string if no valid values
 */
export function joinValues(values: unknown, separator = ', '): string {
  if (values === null || values === undefined) {
    return ''
  }

  const arrayValues = Array.isArray(values) ? values : [values]

  const flattened: unknown[] = []

  arrayValues.forEach(item => {
    if (Array.isArray(item)) {
      item.forEach(inner => flattened.push(inner))
    } else {
      flattened.push(item)
    }
  })

  const cleaned = flattened
    .filter(item => item !== null && item !== undefined)
    .map(item => String(item).trim())
    .filter(item => item.length > 0)

  return cleaned.join(separator)
}

/**
 * Formats a camelCase or PascalCase string by adding spaces before capital letters.
 * Useful for converting code identifiers to human-readable labels.
 *
 * @param text The text to format (e.g., 'BugFixing', 'CodeGeneration')
 * @returns Formatted text with spaces (e.g., 'Bug Fixing', 'Code Generation')
 *
 * @example
 * formatCamelCaseToSpaced('BugFixing') // Returns 'Bug Fixing'
 * formatCamelCaseToSpaced('CodeGeneration') // Returns 'Code Generation'
 * formatCamelCaseToSpaced('Testing') // Returns 'Testing'
 */
export function formatCamelCaseToSpaced(text: string): string {
  if (!text || typeof text !== 'string') {
    return ''
  }

  return text.replace(/([A-Z])/g, '$1').trim()
}

/**
 * Helper function to transform Azure DevOps API URL to user-friendly format for direct access
 * @param url The original API URL
 * @returns A transformed URL that opens the work item in the Azure DevOps web interface
 */
export const updateMainADOURL = (url: string): string => {
  return url?.replace('_apis/wit/workItems', '_workItems/edit') || url
}

/**
 * Creates a Redux selector to retrieve team configuration values
 *
 * @template T Type of the configuration value to return
 * @param componentId The team's component ID
 * @param configKey The name of the configuration property to retrieve (e.g., 'targetVelocity', 'onTimeDelivery')
 * @param defaultValue Default value to return if the config value isn't found or is invalid
 * @returns A Redux selector function that returns the team configuration value
 */
export function getTeamConfig<T>(componentId: string | null, configKey: string, defaultValue: T): (state: any) => T {
  return (state: any) => {
    const teams = state.team?.teams || []
    const team = teams.find((t: any) => t.componentId === componentId)

    // Handle numeric values with validation (including string numbers from API)
    if (typeof defaultValue === 'number') {
      const value = team?.[configKey]
      if (value === null || value === undefined) {
        return defaultValue
      }

      // Convert string numbers to actual numbers
      const numericValue = typeof value === 'string' ? parseFloat(value) : value

      // Return the numeric value if it's valid, otherwise return default
      return typeof numericValue === 'number' && !isNaN(numericValue) ? numericValue : defaultValue
    }

    // Handle other types
    return team?.[configKey] !== undefined ? team[configKey] : defaultValue
  }
}

export function getCurrentMonthYear(): string {
  const now = new Date()
  const month = now.toLocaleString('default', {month: 'long'})
  const year = now.getFullYear()
  return `${month} ${year}`
}

export function formatDurationInMinutes(durationInMinutes: number) {
  const ms = durationInMinutes * 60 * 1000
  const date = new Date(ms)
  return date.toISOString().slice(11, 19)
}

export function formatDateRangeLabel(label: string): string {
  if (label.includes(' - ')) {
    const [startDate, endDate] = label.split(' - ')
    const format = (dateString: string) => {
      const parts = dateString.split('/')
      if (parts.length >= 2) {
        return `${parts[1]}/${parts[0]}` // Only month/day
      }
      return dateString
    }
    return `${format(startDate)} - ${format(endDate)}`
  }
  // Single date, e.g. 23/05/2025
  const parts = label.split('/')
  if (parts.length >= 2) {
    return `${parts[1]}/${parts[0]}`
  }
  return label
}

export const formatPercentage = (value: number, total: number): string => {
  if (total === 0) return '0'

  const rawPercent = (value / total) * 100

  if (Number.isInteger(rawPercent)) {
    return `${rawPercent}` // e.g., 56
  }

  const decimalPlaces = rawPercent % 1 === 0 ? 0 : (rawPercent * 10) % 1 === 0 ? 1 : 2

  return rawPercent.toFixed(decimalPlaces) // e.g., 87.1 or 87.23
}

/**
 * Formats a number intelligently - returns whole number if no decimal, otherwise up to 2 decimal places
 * Removes trailing zeros after decimal point
 * @param value The number to format
 * @returns Formatted number as string (e.g., "15" or "15.4" or "15.38")
 */
export const formatNumber = (value: number): string => {
  // Integer case: no decimals in input
  if (Number.isInteger(value)) {
    return value.toString()
  }

  // Has decimals → round to max 2 decimal places
  const rounded = Number(value?.toFixed(2))

  // Convert back to string
  const str = rounded.toString()

  // If rounding makes it an integer (e.g., 1.00 → 1), return integer
  if (Number.isInteger(rounded)) {
    return rounded.toString()
  }

  return str // already correct (e.g., "1.1", "1.47")
}

interface PercentValueOptions {
  /**
   * Render whole numbers with two decimals too (100 -> "100.00%"). Reusability displays its
   * percentage this way; the other SE Metrics cards show whole numbers bare.
   */
  alwaysTwoDecimals?: boolean
}

/**
 * Renders a metric as a percentage: whole numbers bare, anything else to two decimal places.
 *
 * This is deliberately NOT formatNumber — formatNumber strips trailing zeros ("14.5"), whereas the
 * benchmark cards pad ("14.50%"). Callers that need a leading '~' for manually-entered values
 * prepend it themselves.
 */
export const formatPercentValue = (value: number, {alwaysTwoDecimals = false}: PercentValueOptions = {}): string => {
  const formatted = !alwaysTwoDecimals && Number.isInteger(value) ? `${value}` : value.toFixed(2)

  return `${formatted}%`
}

/**
 * Formats a value as a whole number (no decimals)
 * Safely handles null, undefined, NaN, and non-numeric values
 * @param value The value to format (can be number, string, null, undefined)
 * @returns Formatted whole number as string (e.g., "15", "0")
 */
export const formatWholeNumber = (value: number | string | null | undefined): string => {
  const numValue = Number(value)
  return Math.round(numValue || 0).toString()
}

/**
 * Formats a number with comma separators for thousands
 * @param value The number to format
 * @returns Formatted number with commas (e.g., "37,941", "423,328")
 */
export const formatNumberWithCommas = (value: number): string => {
  return (value || 0).toLocaleString('en-US')
}

// Functions moved from formatters.ts
export const formatCurrency = (amount: number, prefix = '$'): string => {
  if (amount < 1e3) return `${prefix}${amount}`
  if (amount >= 1e3 && amount < 1e6) return `${prefix}${+(amount / 1e3).toFixed(1)}K`
  if (amount >= 1e6 && amount < 1e9) return `${prefix}${+(amount / 1e6).toFixed(1)}M`
  if (amount >= 1e9 && amount < 1e12) return `${prefix}${+(amount / 1e9).toFixed(1)}B`
  if (amount >= 1e12) return `${prefix}${+(amount / 1e12).toFixed(1)}T`
  return `${prefix}${amount}`
}

export const formatDate = (date: Date, format: string): string => {
  const options: Intl.DateTimeFormatOptions = {}

  if (format.includes('MMMM')) {
    options.month = 'long'
  }
  if (format.includes('yyyy')) {
    options.year = 'numeric'
  }

  return new Intl.DateTimeFormat('en-US', options).format(date)
}

export const getCurrentMonth = (): string => {
  return formatDate(new Date(), 'MMMM yyyy')
}

/**
 * Sorts an array of YYYY-MM format date strings chronologically
 * @param months Array of month strings in YYYY-MM format (e.g., ["2024-01", "2024-12", "2025-01"])
 * @returns Sorted array in chronological order (earliest to latest)
 */
export const sortMonthsChronologically = (months: string[]): string[] => {
  return [...months].sort((a, b) => {
    // For YYYY-MM format, lexicographic sorting works correctly
    // but this makes the intent explicit and handles edge cases
    const [yearA, monthA] = a.split('-').map(Number)
    const [yearB, monthB] = b.split('-').map(Number)

    if (yearA !== yearB) {
      return yearA - yearB
    }
    return monthA - monthB
  })
}

/**
 * Sorts an array of strings alphabetically using localeCompare
 * Useful for sorting names, labels, or any string values
 * @param strings Array of strings to sort
 * @returns Sorted array in alphabetical order
 *
 * @example
 * sortStringsAlphabetically(['John', 'Alice', 'Bob']) // Returns ['Alice', 'Bob', 'John']
 * sortStringsAlphabetically(['2024', '2023', '2025']) // Returns ['2023', '2024', '2025']
 */
export const sortStringsAlphabetically = (strings: string[]): string[] => {
  return [...strings].sort((a, b) => a.localeCompare(b))
}

/**
 * Sorts an array of ISO date strings (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss) chronologically
 * @param dates Array of ISO date strings
 * @returns Sorted array in chronological order (earliest to latest)
 *
 * @example
 * sortDateStringsChronologically(['2024-12-01', '2024-01-15', '2024-06-30'])
 * // Returns ['2024-01-15', '2024-06-30', '2024-12-01']
 */
export const sortDateStringsChronologically = (dates: string[]): string[] => {
  return [...dates].sort((a, b) => a.localeCompare(b))
}

/**
 * Downloads a Blob as an Excel (.xlsx) file with a configurable filename.
 * @param fetchBlob - Async function that fetches the Blob from the API
 * @param fileName - The name for the downloaded file (without extension)
 * @returns Promise that resolves when download completes
 */
export const downloadExcelFile = async (fetchBlob: () => Promise<Blob>, fileName: string): Promise<void> => {
  const blob = await fetchBlob()
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${fileName}.xlsx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(url)
}

export const lazyLoad = <T extends ComponentType<any>>(
  importFunc: () => Promise<{default: T}>,
): React.LazyExoticComponent<T> => {
  return lazy(importFunc)
}

/**
 * Sorts an array of objects by year and month properties chronologically
 * @param data Array of objects containing year and month properties
 * @returns A new sorted array in chronological order (earliest to latest)
 */
export const sortDataByYearMonth = <T extends {year: number; month: number}>(data: T[]): T[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return []
  }

  return [...data].sort((a, b) => {
    const dateA = new Date(a.year, a.month - 1) // Month is 0-indexed in Date
    const dateB = new Date(b.year, b.month - 1)
    return dateA.getTime() - dateB.getTime()
  })
}

/**
 * Formats yearMonth string (e.g., "2025-10") to short month name (e.g., "Oct")
 * @param yearMonth Year-month string in format "YYYY-MM"
 * @returns Short month name (e.g., "Oct")
 */
export function formatYearMonthToShortName(yearMonth: string): string {
  const [, month] = yearMonth.split('-')
  return getMonthShortName(month)
}

/**
 * Gets short month name from month number (1-12)
 * @param month Month number (1-12) or string
 * @returns Short month name (e.g., "Jan", "Feb")
 */
export function getMonthShortName(month: string | number): string {
  const monthNum = Number(month)
  if (monthNum >= 1 && monthNum <= 12) {
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][monthNum - 1]
  }
  return String(month)
}

/**
 * Helper function to extract short month name from category string
 * Converts "Sep 2025" to "Sep", "Oct 2025" to "Oct", etc.
 * @param category - Category string in format "MMM YYYY"
 * @returns Short month name (e.g., "Sep")
 */
export function extractShortMonthName(category: string): string {
  const parts = category.split(' ')
  return parts[0] // Return just the month name
}

/**
 * Get date picker configuration with disabled date ranges
 * @param startYear - The year from which dates should be enabled (e.g., 2024 means enable from Jan 2024)
 * @returns Object with beforeDate and afterDate for pickerDisabled prop
 */
export const getDatePickerConfig = (startYear: number) => ({
  beforeDate: new Date(`${startYear - 1}-12-31T12:00:00.000Z`), // Disable dates up to midday Dec 31 of previous year
  afterDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0), // Last day of current month
})

const ALLOWED_ALPHANUMERIC_SPACE_KEYS = /^[a-zA-Z0-9 ]$/
const ALLOWED_CONTROL_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
])

/**
 * Prevents typing of special characters and allows only letters, numbers and spaces.
 * Keeps navigation/edit shortcuts intact.
 */
export const preventSpecialCharsKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
  if (ALLOWED_CONTROL_KEYS.has(e.key)) return
  if (e.ctrlKey || e.metaKey) return

  if (!ALLOWED_ALPHANUMERIC_SPACE_KEYS.test(e.key)) {
    e.preventDefault()
  }
}

/**
 * Strips characters that are not alphanumeric or space from a string.
 * Use this as a keypress / onChange filter for fields that forbid special characters.
 * @param value Raw input string
 * @returns String containing only letters (a-z, A-Z), digits (0-9) and spaces
 */
export const stripSpecialChars = (value: string): string => value.replace(/[^a-zA-Z0-9 ]/g, '')

/**
 * Scrolls the window to the top of the page.
 */
export const scrollToTop = (): void => {
  window.scrollTo({top: 0, behavior: 'instant'})
}

/**
 * Smoothly scrolls to a DOM element by its id, accounting for a fixed header offset.
 * @param id   The element id to scroll to
 * @param headerOffset  Pixels to subtract from the top (default 80)
 */
export const scrollToElement = (id: string, headerOffset = 80): void => {
  const el = document.getElementById(id)
  if (!el) return
  const top = el.getBoundingClientRect().top + window.scrollY - headerOffset
  window.scrollTo({top, behavior: 'smooth'})
}

export const formatDateTime = (value: string): string => {
  if (!value) return ''

  const date = new Date(value)

  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()

  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')

  return `${month}/${day}/${year} (${hours}:${minutes}:${seconds})`
}

export const formatDateToUS = (dateString?: string): string => {
  if (!dateString) return '-'

  const date = new Date(dateString)

  // Hardcoded en-US + UTC to ensure consistency across all users
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'UTC', // 🔥 critical for consistency across regions
  }).format(date)
}
